import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import clsx from 'clsx';
import {
  Upload, File as FileIcon, Trash2, Pencil, X, Sparkles, BookOpen, ShieldCheck, ExternalLink, FolderOpen,
  CheckCircle2, AlertTriangle, Loader2,
} from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useConfirm } from '../../components/ConfirmDialog';
import {
  DataTable, Dialog, Field, FormRow, ErrorState, TableSkeleton, fmtDate, plural,
} from '../../components/ui';
import StaffHeader from './StaffHeader';
import {
  useStaffRecord, useStableCallback, ExpiryBadge, IconButton, expiryStatus, expiryText, expirySortKey,
  docHref, docName, forgetStaffRecord, roleLabel,
} from './hrKit';

const DOC_TYPES = [
  'NDIS Clearance', 'Police Check', 'Working with Children', 'First Aid',
  'CPR Certificate', 'Manual Handling', 'Contract', 'Identification', 'Payroll', 'Other',
];
const MAX_MB = 15;
const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

// A document "matches" a requirement when its category or name contains the requirement's name.
const matchesRequirement = (doc, reqName) => {
  const r = norm(reqName);
  if (!r) return false;
  return norm(doc.category).includes(r) || norm(doc.document_type).includes(r) || norm(docName(doc)).includes(r);
};

/* ── Edit document ─────────────────────────────────────────────────────── */

function EditDocDialog({ doc, categories, onSaved, onClose }) {
  const [form, setForm] = useState({ name: '', documentType: '', expiryDate: '' });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!doc) return;
    setError('');
    setForm({ name: docName(doc), documentType: doc.document_type || doc.category || '', expiryDate: doc.expiry_date ? String(doc.expiry_date).slice(0, 10) : '' });
  }, [doc]);
  const close = useStableCallback(() => { if (!saving) onClose(); });

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setError('Enter a document name.'); return; }
    setSaving(true);
    try {
      await api.put(`/staff/documents/${doc.id}`, form);
      toast.success('Document updated');
      setSaving(false);
      onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || "The changes weren't saved. Try again.");
      setSaving(false);
    }
  };

  const options = [...new Set([...categories, form.documentType].filter(Boolean))];
  return (
    <Dialog open={!!doc} onClose={close} title="Edit document"
      footer={(
        <>
          <button type="button" onClick={close} className="btn-secondary" disabled={saving}>Cancel</button>
          <button type="submit" form="staff-doc-edit" className="btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</button>
        </>
      )}>
      <form id="staff-doc-edit" onSubmit={save} className="space-y-4">
        <Field label="Document name" required error={error}>
          {(id) => <input id={id} className="input-field" value={form.name} onChange={(e) => { setForm((f) => ({ ...f, name: e.target.value })); setError(''); }} />}
        </Field>
        <FormRow>
          <Field label="Category">
            {(id) => (
              <select id={id} className="input-field" value={form.documentType} onChange={(e) => setForm((f) => ({ ...f, documentType: e.target.value }))}>
                <option value="">No category</option>
                {options.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            )}
          </Field>
          <Field label="Expiry date" hint="Leave blank if it doesn't expire.">
            {(id) => <input id={id} type="date" className="input-field" value={form.expiryDate} onChange={(e) => setForm((f) => ({ ...f, expiryDate: e.target.value }))} />}
          </Field>
        </FormRow>
      </form>
    </Dialog>
  );
}

/* ── Upload: files first, then category and expiry ─────────────────────── */

function UploadDialog({ files, staffId, categories, preset, onDone, onClose }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!files || !files.length) { setRows([]); return; }
    setRows(files.map((f, i) => ({
      key: `${f.name}-${f.size}-${i}`, file: f,
      category: preset || '', expiryDate: '',
      state: f.size > MAX_MB * 1024 * 1024 ? 'error' : 'ready',
      message: f.size > MAX_MB * 1024 * 1024 ? `Larger than ${MAX_MB} MB` : '',
    })));
  }, [files, preset]);
  const setRow = (key, patch) => setRows((list) => list.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const close = useStableCallback(() => { if (!busy) onClose(); });
  const ready = rows.filter((r) => r.state === 'ready');

  const upload = async () => {
    setBusy(true);
    const results = [];
    for (const r of rows) {
      if (r.state !== 'ready') continue;
      setRow(r.key, { state: 'uploading', message: '' });
      const fd = new FormData();
      fd.append('file', r.file);
      fd.append('staffId', staffId);
      if (r.category) fd.append('category', r.category);
      if (r.expiryDate) fd.append('expiryDate', r.expiryDate);
      // Uploading against a required check records it on the worker (as pending verification),
      // so the "Missing" slot fills in — the same thing the mobile app does.
      if (preset && r.category === preset) fd.append('qualificationName', preset);
      try {
        const { data } = await api.post('/staff/documents', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        setRow(r.key, { state: 'done' });
        results.push({ ...data, fileName: r.file.name });
      } catch (e) {
        setRow(r.key, { state: 'error', message: e.response?.data?.message || 'Upload failed' });
      }
    }
    setBusy(false);
    const failed = rows.length - results.length;
    if (results.length) toast.success(`${plural(results.length, 'document')} uploaded`);
    onDone(results, failed > 0);
  };

  const options = [...new Set([...categories, preset].filter(Boolean))];
  const allSettled = rows.length > 0 && rows.every((r) => r.state === 'done' || r.state === 'error');
  return (
    <Dialog open={!!(files && files.length)} onClose={close} size="lg"
      title={files && files.length === 1 ? 'Upload document' : `Upload ${files ? files.length : 0} documents`}
      description="Leave the category or expiry blank and we'll read them from the document where we can."
      footer={(
        <>
          <button type="button" onClick={close} className="btn-secondary" disabled={busy}>{allSettled ? 'Close' : 'Cancel'}</button>
          {!allSettled && (
            <button type="button" onClick={upload} className="btn-primary" disabled={busy || !ready.length}>
              {busy ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : <Upload size={14} aria-hidden="true" />}
              {busy ? 'Uploading…' : `Upload ${ready.length || ''}`.trim()}
            </button>
          )}
        </>
      )}>
      <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
        {rows.map((r) => (
          <li key={r.key} className="space-y-2 px-3 py-3">
            <div className="flex items-center gap-2">
              <FileIcon size={14} className="shrink-0 text-slate-400" aria-hidden="true" />
              <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-800">{r.file.name}</span>
              {r.state === 'uploading' && <span className="inline-flex items-center gap-1 text-[12.5px] text-slate-500"><Loader2 size={13} className="animate-spin" aria-hidden="true" />Uploading…</span>}
              {r.state === 'done' && <span className="inline-flex items-center gap-1 text-[12.5px] text-emerald-700"><CheckCircle2 size={13} aria-hidden="true" />Uploaded</span>}
              {r.state === 'error' && <span className="inline-flex items-center gap-1 text-[12.5px] text-rose-700" role="alert"><AlertTriangle size={13} aria-hidden="true" />{r.message}</span>}
              {r.state === 'ready' && !busy && (
                <IconButton label={`Remove ${r.file.name}`} icon={X} onClick={() => setRows((list) => list.filter((x) => x.key !== r.key))} />
              )}
            </div>
            {(r.state === 'ready' || r.state === 'uploading') && (
              <FormRow>
                <Field label="Category">
                  {(id) => (
                    <select id={id} className="input-field" value={r.category} disabled={busy} onChange={(e) => setRow(r.key, { category: e.target.value })}>
                      <option value="">Work it out from the document</option>
                      {options.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  )}
                </Field>
                <Field label="Expiry date">
                  {(id) => <input id={id} type="date" className="input-field" value={r.expiryDate} disabled={busy} onChange={(e) => setRow(r.key, { expiryDate: e.target.value })} />}
                </Field>
              </FormRow>
            )}
          </li>
        ))}
      </ul>
    </Dialog>
  );
}

/* ── Page ──────────────────────────────────────────────────────────────── */

export default function StaffDocuments() {
  const { id } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [params, setParams] = useSearchParams();
  const { staff, error: staffError, reload: reloadStaff } = useStaffRecord(id);
  const [docs, setDocs]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [editing, setEditing]   = useState(null);
  const [pending, setPending]   = useState(null);     // { files, preset }
  const [aiResults, setAiResults] = useState([]);
  const [dragging, setDragging] = useState(false);
  const dragDepth = useRef(0);
  const fileRef = useRef();
  const presetRef = useRef('');
  const wanted = params.get('upload') || '';

  const loadDocs = useCallback(() => {
    setLoading(true);
    setLoadError(null);
    return api.get(`/staff/documents?staffId=${id}&includeExpired=true`)
      .then((r) => setDocs(Array.isArray(r.data) ? r.data : []))
      .catch((e) => setLoadError(e.response?.data?.message || "We couldn't load the documents. Check your connection, then try again."))
      .finally(() => setLoading(false));
  }, [id]);
  useEffect(() => { loadDocs(); }, [loadDocs]);

  const reload = () => { forgetStaffRecord(id); reloadStaff(); loadDocs(); };

  const requirements = useMemo(() => (staff && staff.compliance && staff.compliance.items) || [], [staff]);
  const reqNames = requirements.map((r) => r.qualification_name);
  const categories = [...new Set([...reqNames, ...DOC_TYPES])];

  // Each required check gets a slot; the documents that belong to one sit in it.
  const { slots, others } = useMemo(() => {
    const used = new Set();
    const s = requirements.map((req) => {
      const matched = docs.filter((d) => matchesRequirement(d, req.qualification_name))
        .sort((a, b) => expirySortKey(b.expiry_date) - expirySortKey(a.expiry_date));
      matched.forEach((d) => used.add(d.id));
      return { req, docs: matched };
    });
    const o = docs.filter((d) => !used.has(d.id)).sort((a, b) => expirySortKey(a.expiry_date) - expirySortKey(b.expiry_date));
    return { slots: s, others: o };
  }, [requirements, docs]);

  const openPicker = (preset = '') => { presetRef.current = preset; fileRef.current?.click(); };
  const onPicked = (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length) setPending({ files, preset: presetRef.current });
    presetRef.current = '';
  };

  const deleteDoc = async (d) => {
    if (!await confirm(`${docName(d)} will be deleted from this staff member's record. This can't be undone.`, { title: 'Delete document?', confirmLabel: 'Delete', danger: true })) return;
    try {
      await api.delete(`/staff/documents/${d.id}`);
      toast.success('Document deleted');
      setDocs((list) => list.filter((x) => x.id !== d.id));
      forgetStaffRecord(id);
    } catch (e) { toast.error(e.response?.data?.message || "Couldn't delete the document. Try again."); }
  };

  const clearWanted = () => { const p = new URLSearchParams(params); p.delete('upload'); setParams(p, { replace: true }); };

  // The whole tab takes a drop while files are dragged over it.
  const hasFiles = (e) => Array.from((e.dataTransfer && e.dataTransfer.types) || []).includes('Files');
  const dragHandlers = {
    onDragEnter: (e) => { if (!hasFiles(e)) return; e.preventDefault(); dragDepth.current += 1; setDragging(true); },
    onDragOver: (e) => { if (hasFiles(e)) e.preventDefault(); },
    onDragLeave: (e) => { if (!hasFiles(e)) return; dragDepth.current = Math.max(0, dragDepth.current - 1); if (!dragDepth.current) setDragging(false); },
    onDrop: (e) => {
      if (!hasFiles(e)) return;
      e.preventDefault(); dragDepth.current = 0; setDragging(false);
      const files = Array.from(e.dataTransfer.files || []);
      if (files.length) setPending({ files, preset: '' });
    },
  };

  const docActions = (d) => (
    <span className="flex items-center justify-end">
      {docHref(d) && <IconButton label={`Open ${docName(d)}`} icon={ExternalLink} href={docHref(d)} target="_blank" rel="noreferrer" />}
      <IconButton label={`Edit ${docName(d)}`} icon={Pencil} onClick={() => setEditing(d)} />
      <IconButton label={`Delete ${docName(d)}`} icon={Trash2} danger onClick={() => deleteDoc(d)} />
    </span>
  );
  const docLink = (d) => (docHref(d)
    ? <a href={docHref(d)} target="_blank" rel="noreferrer" className="truncate font-medium text-slate-900 hover:text-primary-700 hover:underline">{docName(d)}</a>
    : <span className="truncate font-medium text-slate-900">{docName(d)}</span>);

  const columns = [
    { key: 'name', header: 'Document', sortable: true, sortValue: (d) => docName(d).toLowerCase(), render: (d) => (
      <span className="flex min-w-0 items-center gap-2"><FileIcon size={14} className="shrink-0 text-slate-400" aria-hidden="true" />{docLink(d)}</span>
    ) },
    { key: 'category', header: 'Category', sortable: true, sortValue: (d) => d.document_type || d.category || null,
      render: (d) => (d.document_type || d.category) && (d.document_type || d.category) !== 'Shared' ? (d.document_type || d.category) : <span className="text-slate-400">—</span> },
    { key: 'expiry', header: 'Expiry', sortable: true, sortValue: (d) => expirySortKey(d.expiry_date), render: (d) => <ExpiryBadge date={d.expiry_date} /> },
    { key: 'uploaded', header: 'Uploaded', sortable: true, sortValue: (d) => d.created_at || null, render: (d) => <span className="whitespace-nowrap tabular-nums text-slate-600">{d.created_at ? fmtDate(d.created_at) : '—'}</span> },
    { key: 'actions', header: <span className="sr-only">Actions</span>, align: 'right', render: docActions },
  ];

  const expiredCount = docs.filter((d) => expiryStatus(d.expiry_date).state === 'expired').length;
  const soonCount = docs.filter((d) => expiryStatus(d.expiry_date).state === 'soon').length;

  return (
    <div {...dragHandlers} className="relative">
      <StaffHeader staff={staff} error={staffError} onRetry={reloadStaff} />
      <input ref={fileRef} type="file" multiple className="hidden" onChange={(e) => { onPicked(e.target.files); e.target.value = ''; }} />

      {dragging && (
        <div className="pointer-events-none absolute inset-0 z-30 flex items-center justify-center rounded-xl border-2 border-dashed border-primary-400 bg-primary-50/80">
          <p className="flex items-center gap-2 text-sm font-semibold text-primary-700"><Upload size={18} aria-hidden="true" />Drop files to upload them for {staff ? staff.first_name : 'this worker'}</p>
        </div>
      )}

      <div className="space-y-4">
        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-[13px] text-slate-600">
            {loading ? 'Loading documents…' : `${plural(docs.length, 'document')}`}
            {!loading && expiredCount > 0 && <span className="font-semibold text-rose-700"> · {expiredCount} expired</span>}
            {!loading && soonCount > 0 && <span className="font-semibold text-amber-700"> · {soonCount} expiring</span>}
          </p>
          <span className="hidden text-[12.5px] text-slate-500 sm:inline">You can also drag files onto this page.</span>
          <button type="button" onClick={() => openPicker('')} className="btn-primary btn-sm ml-auto"><Upload size={14} aria-hidden="true" />Upload documents</button>
        </div>

        {/* AI results: what the upload changed on the record */}
        {aiResults.length > 0 && (
          <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
            <div className="flex items-start gap-3">
              <Sparkles size={16} className="mt-0.5 shrink-0 text-slate-500" aria-hidden="true" />
              <div className="min-w-0 flex-1 space-y-3">
                {aiResults.map((r) => {
                  const ai = r.aiAnalysis || {};
                  const facts = [
                    ai.documentType && ['Type', ai.documentType],
                    ai.category && ['Category', ai.category],
                    ai.expiryDate && ['Expiry', fmtDate(ai.expiryDate)],
                    ai.issueDate && ['Issued', fmtDate(ai.issueDate)],
                  ].filter(Boolean);
                  return (
                    <div key={r.id || r.fileName}>
                      <p className="text-[13px] font-semibold text-slate-900">Read from {r.fileName}</p>
                      {facts.length > 0 && (
                        <dl className="mt-1 flex flex-wrap gap-x-6 gap-y-1 text-[13px]">
                          {facts.map(([k, v]) => <div key={k}><dt className="inline text-slate-500">{k}: </dt><dd className="inline font-medium text-slate-800">{v}</dd></div>)}
                        </dl>
                      )}
                      <div className="mt-1.5 flex flex-wrap gap-2">
                        {r.createdTraining && (
                          <button type="button" onClick={() => navigate(`/staff/${id}/training`)} className="btn-secondary btn-sm">
                            <BookOpen size={13} aria-hidden="true" />Training added: {r.createdTraining.training_name}
                          </button>
                        )}
                        {r.createdQualification && (
                          <span className="badge-gray"><ShieldCheck size={12} aria-hidden="true" />Qualification linked: {r.createdQualification.qualification_name}</span>
                        )}
                        {Object.keys(r.fieldsUpdated || {}).length > 0 && (
                          <span className="badge-gray"><ShieldCheck size={12} aria-hidden="true" />Profile updated: {Object.keys(r.fieldsUpdated).map((k) => k.replace(/_/g, ' ')).join(', ')}</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              <IconButton label="Dismiss" icon={X} onClick={() => setAiResults([])} />
            </div>
          </div>
        )}

        {/* Required checks for the role */}
        {staff && requirements.length > 0 && (
          <section aria-labelledby="req-heading" className="rounded-xl border border-slate-200 bg-white">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-5 py-3">
              <h2 id="req-heading" className="text-[15px] font-semibold text-slate-900">Required for {roleLabel(staff.role).toLowerCase()}s</h2>
              {wanted && <button type="button" onClick={clearWanted} className="text-[12.5px] font-medium text-slate-500 hover:text-slate-800 hover:underline">Clear highlight</button>}
            </div>
            <ul className="divide-y divide-slate-100">
              {slots.map(({ req, docs: matched }) => {
                const ex = expiryStatus(req.expiry_date);
                const state = !req.held ? 'missing' : ex.state === 'expired' ? 'expired' : ex.state === 'soon' ? 'soon' : 'current';
                const highlight = wanted && norm(wanted) === norm(req.qualification_name);
                const pill = {
                  missing: <span className="badge-red">Missing</span>,
                  expired: <span className="badge-red">{expiryText(req.expiry_date)}</span>,
                  soon: <span className="badge-yellow">{expiryText(req.expiry_date)}</span>,
                  current: <ExpiryBadge date={req.expiry_date} none="Current" />,
                }[state];
                const action = state === 'missing' ? 'Upload' : (state === 'current' ? null : 'Upload renewal');
                return (
                  <li key={req.qualification_name} className={clsx('px-5 py-3', highlight && 'bg-primary-50/60 ring-2 ring-inset ring-primary-300')}>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                      <span className="min-w-0 flex-1 text-sm font-medium text-slate-800">{req.qualification_name}</span>
                      {pill}
                      {action ? (
                        <button type="button" onClick={() => openPicker(req.qualification_name)} className={state === 'missing' ? 'btn-primary btn-sm' : 'btn-secondary btn-sm'}
                          autoFocus={!!highlight}>
                          <Upload size={13} aria-hidden="true" />{action}
                        </button>
                      ) : (
                        <button type="button" onClick={() => openPicker(req.qualification_name)} className="btn-ghost btn-sm">
                          <Upload size={13} aria-hidden="true" />Add file
                        </button>
                      )}
                    </div>
                    {matched.length > 0 && (
                      <ul className="mt-2 space-y-1">
                        {matched.map((d) => (
                          <li key={d.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg bg-slate-50 px-3 py-1.5 text-[13px]">
                            <FileIcon size={13} className="shrink-0 text-slate-400" aria-hidden="true" />
                            <span className="flex min-w-0 flex-1">{docLink(d)}</span>
                            <ExpiryBadge date={d.expiry_date} />
                            {docActions(d)}
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        )}
        {staff && requirements.length === 0 && (
          <p className="text-[12.5px] text-slate-500">
            No required checks are set for {roleLabel(staff.role).toLowerCase()}s, so every file is listed below. Set them in <Link to="/settings" className="font-medium text-primary-700 hover:underline">Settings, Compliance requirements</Link>.
          </p>
        )}

        {/* Everything else */}
        <section aria-labelledby="docs-heading" className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <div className="border-b border-slate-100 px-5 py-3">
            <h2 id="docs-heading" className="text-[15px] font-semibold text-slate-900">{requirements.length ? 'Other documents' : 'Documents'}</h2>
          </div>
          {loading ? <TableSkeleton rows={4} cols={4} />
            : loadError ? <ErrorState message={loadError} onRetry={loadDocs} />
              : (
                <DataTable
                  label={requirements.length ? 'Other documents' : 'Documents'}
                  columns={columns}
                  rows={others}
                  stickyHeader={false}
                  empty={docs.length === 0
                    ? { icon: FolderOpen, title: 'No documents yet', hint: 'Upload a police check, NDIS screening and first aid certificate to get started.', action: <button type="button" onClick={() => openPicker('')} className="btn-primary btn-sm"><Upload size={14} aria-hidden="true" />Upload documents</button> }
                    : { icon: FolderOpen, title: 'Nothing else on file', hint: 'Every document here belongs to one of the required checks above.' }}
                />
              )}
        </section>
      </div>

      <EditDocDialog doc={editing} categories={categories} onSaved={reload} onClose={() => setEditing(null)} />
      <UploadDialog
        files={pending && pending.files}
        preset={pending && pending.preset}
        staffId={id}
        categories={categories}
        onClose={() => setPending(null)}
        onDone={(results, hadFailures) => {
          const withAi = results.filter((r) => r.aiAnalysis && (r.aiAnalysis.expiryDate || r.aiAnalysis.qualificationName || r.createdTraining || r.aiAnalysis.documentType));
          if (withAi.length) setAiResults(withAi);
          if (results.length) { reload(); if (wanted) clearWanted(); }
          if (!hadFailures) setPending(null);
        }}
      />
    </div>
  );
}
